/**
 * Created by Simon Zhao on 01/27/2022.
 * The model serves as the in-parameter of the diagram conditional gateway dialog component and the note trigger filter dialog component.
 * ------ maintenance history ------
 * Updated by Daniel Wang on 5/22/2023, Added field type user
 */

import { KeyValue } from '@angular/common';
import { ControlType } from '../tamalelibs/models/template-control.model';

export const fieldOperatorMap = {
    entity: [{ key: 'includeEntity', value: 'Include' }, { key: 'notIncludeEntity', value: 'Not include' }],
    text: [{ key: 'containsText', value: 'Contain' }, { key: 'notContainsText', value: 'Not contain' }, { key: 'equalsText', value: 'Equal to' }, { key: 'notEqualsText', value: 'Not equal to' }],
    number: [{ key: 'greaterThanNumber', value: 'Greater than' }, { key: 'greaterThanOrEqualsNumber', value: 'Greater than or equal to' }, { key: 'lessThanNumber', value: 'Less than' }, { key: 'lessThanOrEqualsNumber', value: 'Less than or equal to' }, { key: 'equalsNumber', value: 'Equal to' }, { key: 'notEqualsNumber', value: 'Not equal to' }],
    date: [{ key: 'equalsText', value: 'Equal to' }],
    user: [{ key: 'includeEntity', value: 'Include' }, { key: 'notIncludeEntity', value: 'Not include' }],
};

export class ConditionFieldItem {
    fieldType: 'entity' | 'text' | 'number' | 'date' | 'user';
    displayText: string;
    fieldId: string;
    fieldConfig: any;
    static convertFieldType(fldType: ControlType): 'entity' | 'text' | 'number' | 'date' | 'user' {
        switch (fldType) {
            case ControlType.NUMBER:
                return 'number';
            case ControlType.MULTI_ENTITY_DROP_DOWN:
            case ControlType.SINGLE_ENTITY_DROP_DOWN:
                return 'entity';
            case ControlType.DATE:
                return 'date';
            case ControlType.SINGLE_USER_DROP_DOWN:
                return 'user';
            default:
                return 'text';
        }
    }
}


export class DiagramConditionModel {
    static readonly conditionDelimiter = '&&';

    fieldId: any;
    operator: KeyValue<string, string>;
    expectedValue: any;
    fieldType: string;
    fieldCandidates: Array<ConditionFieldItem> = [];
    fieldConfig: any;
    operatorCandidates: Array<KeyValue<string, string>> = [];

    static isNumberOperator(operatorKey: string) {
        return fieldOperatorMap.number.some(kvPair => kvPair.key === operatorKey);
    }

    /**
     * creates a diagram condition model instance from a condition string.
     * @param condition the condition string.
     * @returns the diagram condition model instance.
     */
    static import(condition: string): DiagramConditionModel[] {
        // "conditionSpEL": "#greaterThanNumber('i3e1exf9', 40, ##placeHolder##) || #lessThanNumber('i3e1exf9', 10, ##placeHolder##) "
        // "conditionSpEL": "#greaterThanNumber('8qvgfxk9', 10, ##placeHolder##) && #containsText('pilb3oxs', 'lisa', ##placeHolder##)"
        // "conditionSpEL": "#includeEntity('qrnyerhv', {'8050b2f2104748628be9e9003d3d38a3','678b26tyh474865rfhye9003dfgthu3'}, ##placeHolder##)"
        // "conditionSpEL": true
        const conditionImportFunc = (singleCondition: string) => {
            const model = new DiagramConditionModel();
            const paraDelimiter = ',';
            const operatorPrefix = '#';
            const operatorSuffix = '(';
            const entityPrefix = '{';
            const entitySuffix = '}';
            const isEntityConditionFunc = (conStr: string) => conStr.indexOf(entityPrefix) >= 0 && conStr.indexOf(entitySuffix) >= 0;
            const getOperatorText = (operatorKey: string) => {
                let operatorText;
                const fldCfgArray = Object.keys(fieldOperatorMap);
                for (let i = 0; i < fldCfgArray.length; i++) {
                    const fldTypeCfg: Array<any> = fieldOperatorMap[fldCfgArray[i]];
                    const matchedPair = fldTypeCfg.find(ksPair => ksPair.key === operatorKey);
                    if (matchedPair) {
                        operatorText = matchedPair.value;
                        break;
                    }
                }
                return operatorText;
            };

            if (isEntityConditionFunc(singleCondition)) {
                const entityParas = singleCondition.split(entityPrefix);
                if (entityParas.length === 2) {
                    const operatorNFieldId = entityParas[0];
                    const operatorNFieldArray = operatorNFieldId.split(operatorSuffix);
                    if (operatorNFieldArray.length === 2) {
                        const operatorStr = operatorNFieldArray[0].replace(operatorPrefix, '');
                        model.operator = { key: operatorStr, value: getOperatorText(operatorStr) };
                        const fieldItem = new ConditionFieldItem();
                        fieldItem.fieldId = operatorNFieldArray[1].replace(/'/g, '').replace(/,/g, '').trim();
                        model.fieldId = fieldItem;
                        const entityNPlaceholder = entityParas[1].split(entitySuffix);
                        if (entityNPlaceholder.length === 2) {
                            const entityArray = entityNPlaceholder[0].split(paraDelimiter);
                            const stripOutSingleQuote = (entityStr: string) => {
                                return { 'id': entityStr.replace(/'/g, '') };
                            };
                            model.expectedValue = entityArray.map(entityStr => stripOutSingleQuote(entityStr));
                        } else {
                            throw new Error('bad format entity condition string');
                        }

                    } else {
                        throw new Error('bad format entity condition string');
                    }
                } else {
                    throw new Error('bad format entity condition string');
                }
            } else {
                const paras = singleCondition.split(paraDelimiter);
                const mergedParas = [];
                if (paras.length > 3) {
                    // might be the case in which value string contains commas.
                    const mergedValuePara = [];
                    for (let i = 1; i < paras.length - 1; i++) {
                        mergedValuePara.push(paras[i]);
                    }
                    mergedParas.push(paras[0]);
                    mergedParas.push(mergedValuePara.join(paraDelimiter));
                    mergedParas.push(paras[paras.length - 1]);
                } else {
                    mergedParas.push(...paras);
                }

                if (mergedParas.length === 3) {
                    const operatorNFieldId = mergedParas[0];
                    const operatorNFieldArray = operatorNFieldId.split(operatorSuffix);
                    if (operatorNFieldArray.length === 2) {
                        const operatorStr = operatorNFieldArray[0].replace(operatorPrefix, '');
                        model.operator = { key: operatorStr, value: getOperatorText(operatorStr) };
                        const fieldItem = new ConditionFieldItem();
                        fieldItem.fieldId = operatorNFieldArray[1].replace(/'/g, '');
                        model.fieldId = fieldItem;
                        const valueStr = mergedParas[1].replace(/'/g, '').trim();
                        model.expectedValue = DiagramConditionModel.isNumberOperator(operatorStr.trim()) ? Number(valueStr) : decodeURIComponent(valueStr);
                        return model;
                    } else {
                        throw new Error('Failed to extract the operator name and the field id!');
                    }
                } else {
                    throw new Error('invalid condition sting');
                }
            }

            return model;
        };

        const conditionStrings = condition.split(DiagramConditionModel.conditionDelimiter);
        return conditionStrings.map(conStr => conditionImportFunc(conStr));
    }

    /**
     * generates a condition string per the given condition item array.
     * @param conditions the condition array.
     * @param emptyValue An optional argument, the value would be returned when no condition item found.
     * @returns the condition strinng.
     */
    static export(conditions: DiagramConditionModel[], emptyValue = 'true'): string {
        let result = '';
        if (conditions.length === 0) {
            result = emptyValue;
        } else {
            conditions.forEach(c => {
                if (result.length > 0) {
                    result += DiagramConditionModel.conditionDelimiter;
                }

                if (Array.isArray(c.expectedValue)) {
                    // an entity condition
                    const valueStr = Array.from(c.expectedValue).map(entity => '\'' + entity.id + '\'').join(',');
                    result += `#${c.operator.key}('${c.fieldId.fieldId}', {${valueStr.trim()}},##placeHolder##)`;
                } else {
                    const valueString = DiagramConditionModel.isNumberOperator(c.operator.key) ? c.expectedValue : ('\'' + c.expectedValue + '\'');
                    result += `#${c.operator.key}('${c.fieldId.fieldId}', ${valueString},##placeHolder##)`;
                }
            });
        }

        return result;
    }

    static isConditionBlank(condition: string) {
        return !condition || condition.toString().toLowerCase() === 'true';
    }

    static isConditionBlankEquivalent(conditions: DiagramConditionModel[]): boolean {
        return conditions.length === 1 && !conditions[0].operator && !conditions[0].fieldId && !conditions[0].expectedValue;
    }

}
